import { useEffect, useState, useCallback, useMemo } from 'react';
import { CreateResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { useQuery } from 'react-query';
import { CallGroupApi, CallGroupTypes } from '@frontend/api-call-group';
import { ForwardingNumberApi } from '@frontend/api-forwarding-number';
import { EditableText } from '@frontend/editable-text';
import { useTranslation } from '@frontend/i18n';
import { useInvalidateQueries } from '@frontend/location-helpers';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { useScopedQuery } from '@frontend/scope';
import { useSettingsNavigate, useSettingsMatch, useNavigationBlocker } from '@frontend/settings-routing';
import { Text, useAlert, useForm, useModalControl } from '@frontend/design-system';
import { NewRingSettings } from '../../components/call-group/ring-settings.new';
import { DeleteButton } from '../../components/common/delete-button';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';
import { CancelEditModal } from '../../utils/cancel-edit.modal';
import { logError } from '../../utils/loggers';

export const CallGroupSettings = () => {
  const { params, search } = useSettingsMatch<{
    Params: { id: string };
    Search: { crid?: string; crn?: string; nid?: string };
  }>();
  const { t } = useTranslation('phone', { keyPrefix: 'call-groups' });
  const invalidateQueries = useInvalidateQueries();
  const { navigate } = useSettingsNavigate();
  const alert = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { modalProps: cancelModalProps, triggerProps: cancelTriggerProps } = useModalControl();

  const { mutate: updateCallGroup } = useMutation(
    (payload: CallGroupTypes.UpdateCallGroupRequest) => {
      return CallGroupApi.updateCallGroup(payload);
    },
    {
      onSuccess: () => {
        invalidateQueries(queryKeys.listCallGroups(settingsTenantLocation?.phoneTenantId ?? ''));
        invalidateQueries(queryKeys.callGroup(params.id ?? ''));
        alert.success(t('Call Group updated successfully.'));
        setInitialCallGroupState(callGroupState);
      },
      onError: (error) => {
        alert.error(t('System failed to update Call Group. Please try again.'));
        logError('CallGroupSettings:updateCallGroupMutation | Failed to update call group', {
          data: { callGroupId: params.id, phoneTenantId: settingsTenantLocation?.phoneTenantId, error },
        });
      },
    }
  );

  // crid, crn and nid are set in the search params when the user is redirected from the call route details page.
  const [callRouteId, callRouteName, nodeId] = [search.crid, search.crn, search.nid];

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.callGroup(params.id),
    queryFn: () => CallGroupApi.readCallGroup({ callGroupId: params.id }),
    meta: {
      error: {
        message: 'CallGroupSettings:callGroupQuery | Failed to fetch call group details',
        data: { callGroupId: params.id, phoneTenantId: settingsTenantLocation?.phoneTenantId },
      },
    },
  });

  const { mutate: deleteCallGroup } = useMutation(
    (payload: CallGroupTypes.DeleteCallgroupTypes) => {
      return CallGroupApi.deleteCallGroup(payload);
    },
    {
      onSuccess: () => {
        invalidateQueries(queryKeys.listCallGroups(settingsTenantLocation?.phoneTenantId ?? ''));
        invalidateQueries(queryKeys.callGroup(params.id ?? ''));
        alert.success(t('Call group deleted successfully.'));
      },
      onError: (error) => {
        alert.error(t('Unable to delete the call group.'));
        logError('CallGroupSettings:deleteCallGroupMutation | Error deleting call group', {
          data: { callGroupId: params.id, phoneTenantId: settingsTenantLocation?.phoneTenantId, error },
        });
      },
    }
  );

  const handleDeleteCallGroup = () => {
    deleteCallGroup({ callGroupId: params.id });
    navigate({ to: '/phone/call-groups' });
  };

  const form = useForm({
    computeChangedValues: true,
    fields: {
      name: { type: 'text' },
    },
  });

  const nameField = form.getFieldProps('name');

  useEffect(() => {
    if (data?.name) {
      form.seedValues({ name: data.name });
    }
  }, [data?.callGroupId, data?.name]);

  const defaultCallGroupState: CreateResponse = {
    name: '',
    ringtone: '',
    locationIds: [],
    callGroupId: '',
    tenantId: '',
    devices: [],
  };

  const [callGroupState, setCallGroupState] = useState<CreateResponse>(defaultCallGroupState);
  const [initialCallGroupState, setInitialCallGroupState] = useState<CreateResponse>(defaultCallGroupState);

  useEffect(() => {
    if (data) {
      const newState = {
        name: data.name ?? '',
        ringtone: data.ringtone ?? '',
        locationIds: data.locationIds ?? [],
        callGroupId: data.callGroupId ?? '',
        tenantId: data.tenantId ?? '',
        devices: data.devices ?? [],
      };
      setCallGroupState(newState);
      setInitialCallGroupState(newState);
    }
  }, [data]);

  useEffect(() => {
    setCallGroupState((prevState) => ({
      ...prevState,
      name: nameField.value,
    }));
  }, [nameField.value]);

  const hasUnsavedChanges = useMemo(() => {
    if (!data || !callGroupState) return false;

    const hasFormChanges = !!form.changedValues;

    const hasStateChanges =
      JSON.stringify({
        ...initialCallGroupState,
        name: nameField.value,
      }) !== JSON.stringify(callGroupState);

    return hasFormChanges || hasStateChanges;
  }, [form.changedValues, initialCallGroupState, callGroupState, nameField.value, data]);

  const { proceed } = useNavigationBlocker(
    {
      shouldBlockFn: () => {
        if (hasUnsavedChanges || cancelModalProps.show) {
          cancelTriggerProps.onClick();
          return true;
        }

        return false;
      },
      enableBeforeUnload: true,
    },
    [hasUnsavedChanges, cancelModalProps.show]
  );

  const handleSave = useCallback(async () => {
    const { devices: callGroupDevices, ...callGroupDetails } = callGroupState;

    await new Promise<void>((resolve, reject) => {
      updateCallGroup(callGroupDetails, {
        onSuccess: () => {
          resolve();
        },
        onError: () => {
          reject(new Error('Failed to update call group'));
        },
      });
    });
  }, [callGroupState, updateCallGroup]);

  const callForwardingNumbers = useQuery({
    queryKey: queryKeys.forwardingNumbers(),
    queryFn: () => ForwardingNumberApi.list({ locationId: settingsTenantLocation?.id }),
    select: (forwardingData) =>
      forwardingData.sort((a, b) => {
        const aName = a.name || '';
        const bName = b.name || '';
        return aName.localeCompare(bName);
      }),
    meta: {
      error: {
        message: 'CallGroupSettings:listForwardingNumbersQuery | Failed to fetch forwarding numbers',
        data: { locationId: settingsTenantLocation?.id },
      },
    },
  });

  return (
    <>
      <PhoneSettingsPage
        title={<EditableText alwaysShowIcon isTitle fieldProps={nameField} />}
        subtitle={t(
          "Assign devices to this group, have them ring simultaneously or in a cascading order, and set each device's ring duration. Note that mobile app devices can't have a delayed start."
        )}
        action={
          data ? (
            <DeleteButton
              id={data.callGroupId}
              name={data.name}
              type='Call Group'
              deleteAction={handleDeleteCallGroup}
            />
          ) : null
        }
        loading={isLoading}
        breadcrumbs={
          callRouteId
            ? [
                { label: t('Phone'), to: '/phone/main' },
                { label: t('Call Routes'), to: '/phone/call-routes' },
                {
                  label: callRouteName ?? '',
                  onClick: () =>
                    navigate({
                      to: '/phone/call-routes/:id',
                      params: { id: callRouteId },
                      context: { contextNodeId: nodeId },
                    }),
                },
                { label: data?.name ?? '', to: '/phone/call-groups/:id', params: { id: data?.callGroupId ?? '' } },
              ]
            : [
                { label: t('Phone'), to: '/phone/main' },
                { label: t('Call Groups'), to: '/phone/call-groups' },
                { label: data?.name ?? '', to: '/phone/call-groups/:id', params: { id: data?.callGroupId ?? '' } },
              ]
        }
        renderContainer={false}
      >
        {data && (
          <NewRingSettings
            callGroup={data}
            setCallGroupState={setCallGroupState}
            callForwardingNumbers={callForwardingNumbers.data ?? []}
            onSave={handleSave}
          />
        )}
        {!isLoading && !data && <Text color='light'>{t('Cannot find call group details')}</Text>}
      </PhoneSettingsPage>

      <CancelEditModal
        disabled={false}
        modalProps={cancelModalProps}
        onQuitClick={proceed}
        onSaveAndQuitClick={() => handleSave().then(() => proceed())}
      />
    </>
  );
};
